import { Form, UploadProps, Image, DatePicker, Divider } from 'antd';
import React, { useEffect, useState } from 'react';
import { scrollToTarget } from '../../../../helpers/DOM';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { ActionResponse } from '../../../../models/actionResponse/types';
import { SelectData } from '../../../../models/common/types';
import { FavoriteType } from '../../../../models/product/enums';
import { Product } from '../../../../models/product/types';
import {
    DraggerText,
    DraggerTextAction,
} from '../../../../pages/projectInfo/tabs/projectInformation/ProjectInformation.styles';
import { uploadFavoriteFileAction } from '../../../../redux/actions/favoriteProductsActions';
import { uploadProductFileAction, getAllActiveDocumentTypesAction } from '../../../../redux/actions/productActions';
import { GetDocumentTypesResponse } from '../../../../redux/actions/productActions/types';
import { alertService } from '../../../../services';
import { blueDark01, blueLink, grayDark03 } from '../../../../styles/colors';
import { BoldText, Text } from '../../../../styles/common.styles';
import CustomInputUploadFileForm from '../../../common/inputs/CustomInputUploadFileForm';
import CustomSelectWithSearch from '../../../common/select/CustomSelectWithSearch';
import { MergedFavoriteProduct } from '../../../favoritProducts/FavoriteProducts.types';
import { UploadFileData, UploadsTableFields } from '../Uploads.types';
import {
    DatePickerContainer,
    SaveUploadsButton,
    UploadChooseFile,
    UploadDocumentTypeSelectContainer,
    UploadDraggerText,
    UploadFileDragger,
    UploadFileInputFlexRow,
    UploadFormSubText,
} from './UploadFileForm.styles';
import { UploadType } from '../UploadsInfo';
import DocumentNameInput from './DocumentNameInput';
import AlternativeTextInput from './AlternativeTextInput';
import DescriptionInput from './DescriptionInput';
import { UploadFileContainer } from '../UploadsInfo.styles';
import { DefinedDocumentTypes } from './UploadFileForm.consts';

const DOCUMENT_TYPE_DIVIDER = "document-type-divider";

interface Props {
    pictureDocumentTypeId: number | undefined;
    favoriteType?: FavoriteType;
    product: MergedFavoriteProduct | Product;
    setNewFile: (file: UploadsTableFields) => void;
    uploadType: UploadType;
    onPending?: (pending: boolean) => void;
}

const UploadFileForm: React.FC<Props> = ({
    pictureDocumentTypeId,
    favoriteType,
    product,
    setNewFile,
    uploadType,
    onPending = () => { },
}) => {
    const dispatch = useAppDispatch();

    const [fileName, setFileName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [form] = Form.useForm();
    const [fileError, setFileError] = useState(false);
    const [pending, setPending] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [showSecondDropdown, setShowSecondDropdown] = useState(false);
    const [allDocumentTypes, setAllDocumentTypes] = useState<Array<GetDocumentTypesResponse>>([]);

    useEffect(() => {
        onPending(pending);
    }, [pending]);

    useEffect(() => {
        let isMounted = true;

        dispatch(getAllActiveDocumentTypesAction())
            .then((res: any) => {
                if (!isMounted) return;

                const payload = res?.payload;
                const items = payload?.data ?? payload ?? [];
                setAllDocumentTypes(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (!isMounted) return;
                alertService.error('Failed to fetch document types');
            });

        return () => {
            isMounted = false;
        };
    }, [dispatch]);

    const onSubmit = async (data: UploadFileData) => {
        try {
            setPending(true);
            if (!file) {
                setFileError(true);
                return;
            }
            if (file.size < 1) {
                alertService.error('0 bytes size file cannot be attached', { fixed: true });
                return;
            }

            let result;
            const fileToSave = file;
            setFile(null);
            const usePictureTypeId = uploadType === UploadType.PICTURE && pictureDocumentTypeId;
            const documentTypeId = usePictureTypeId
                ? pictureDocumentTypeId
                : data.documentTypeId;

            if (favoriteType !== undefined) {
                const { payload } = (await dispatch(
                    uploadFavoriteFileAction({
                        file: fileToSave,
                        documentName: data.documentName,
                        documentTypeId: documentTypeId,
                        alternativeText: data.alternativeText,
                        description: data.description,
                        favoriteType: favoriteType,
                        productFavoriteId: product.id,
                        expirationOn: data?.expirationOn?._d
                            ? new Date(data?.expirationOn?._d).toJSON()
                            : undefined,
                    }),
                )) as ActionResponse<UploadsTableFields>;
                if (payload) {
                    result = payload;
                }
            } else {
                const { payload } = (await dispatch(
                    uploadProductFileAction({
                        productId: product.product.productId,
                        projectProductId: product.product.id,
                        productName: product.productName,
                        projectId: product.product.projectId,
                        file: fileToSave,
                        documentName: data.documentName,
                        documentTypeId: documentTypeId,
                        description: data.description,
                        alternativeText: data.alternativeText,
                        expirationOn: data?.expirationOn?._d
                            ? new Date(data?.expirationOn?._d).toJSON()
                            : undefined,
                    }),
                )) as ActionResponse<UploadsTableFields>;
                if (payload.data) {
                    result = payload;
                }
            }

            if (result) {
                setFileName('');
                form.resetFields();
                setNewFile(result.data);
                alertService.success('New upload was added', { fixed: true });
                const scrollableDiv = document.getElementById('upload-list');
                const targetElement = document.getElementById(result.data.id.toString());
                scrollToTarget(targetElement, scrollableDiv);
            }
        } finally {
            setPending(false);
        }
    };

    const extensionChecker = (value: any) => {
        const allowedPictureExtensions = ['.JPG', '.JPEG', '.GIF', '.PNG'];
        for (const allowedPictureExtension of allowedPictureExtensions) {
            if (value.indexOf(allowedPictureExtension) > -1) {
                return true;
            }
        }
        return false;
    };

    const budgetFile = () => {
        const props: UploadProps = {
            name: 'file',
            multiple: false,
            beforeUpload: () => false,
            onChange(info: any) {
                if (!info?.file) {
                    alertService.error('Something went wrong');
                    return;
                }
                if (info.file.size < 1) {
                    alertService.error('0 bytes size file cannot be attached', { fixed: true });
                    return;
                }
                if (uploadType === 'document' && info.file.size > 52428800) {
                    alertService.error('Files cannot be larger than 50Mb.', { fixed: true });
                    return;
                }
                if (uploadType === 'picture' && info.file.size > 52428800) {
                    alertService.error('Files cannot be larger than 50Mb.', { fixed: true });
                    return;
                }
                if (uploadType === 'picture' && !extensionChecker(info.file.name.toUpperCase())) {
                    alertService.error('.PNG, .JPG, .GIF allowed', { fixed: true });
                    return;
                }
                setFileName(info.file.name);
                form.setFieldValue('documentName', info.file.name);
                setFile(info.file);
                setFileError(false);
            },
        };
        return (
            <>
                {' '}
                <UploadFileDragger
                    multiple={false}
                    showUploadList={false}
                    style={{ marginLeft: -16 + 'px', width: 'calc(100% + 16px)' }}
                    fileList={undefined}
                    {...props}
                    className={fileError ? 'error' : ''}
                >
                    <Image src="src/assets/images/file-lines.svg" preview={false} alt="file" />
                    {!fileName ? (
                        <>
                            <UploadChooseFile weight={400} size={14} height={18} color={blueLink}>
                                Choose {uploadType === 'document' ? 'a document' : 'an image'}
                            </UploadChooseFile>
                            <UploadDraggerText
                                weight={400}
                                size={14}
                                height={18}
                                color={blueDark01}
                            >
                                or drag it here
                            </UploadDraggerText>
                        </>
                    ) : (
                        <UploadDraggerText weight={400} size={14} height={18} color={blueLink}>
                            {fileName}
                        </UploadDraggerText>
                    )}
                </UploadFileDragger>
                {fileName && (
                    <DraggerTextAction
                        multiple={false}
                        showUploadList={false}
                        fileList={undefined}
                        {...props}
                    >
                        <UploadFileInputFlexRow>
                            <BoldText weight={400} size={14} height={18} color={blueLink}>
                                Choose a {uploadType}
                            </BoldText>
                            <Text weight={400} size={14} height={18} color={blueDark01}>
                                or drag it here
                            </Text>
                        </UploadFileInputFlexRow>
                    </DraggerTextAction>
                )}
                <DraggerText
                    style={{ marginLeft: -16 + 'px' }}
                    weight={400}
                    size={12}
                    height={14}
                    color={grayDark03}
                >
                    {uploadType === 'document'
                        ? `Any document type (50Mb maximum)`
                        : `PNG, JPG, GIF (10Mb maximum)`}
                </DraggerText>
            </>
        );
    };

    const changeDocumentName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(event.target.value);
    };

    const onFailed = () => {
        if (!file) {
            setFileError(true);
        }
    };

    const handleCategoryChange = (value: string) => {
        setSelectedCategory(value);
        setShowSecondDropdown(true);
        // Reset document type field when category changes
        form.setFieldValue('documentTypeId', undefined);
    };

    // Get options for second dropdown based on selected category
    const getDocumentTypeOptions = () => {
        if (!selectedCategory) {
            return [];
        }

        let options: SelectData[] = [];
        switch (selectedCategory) {
            case '1': { // CMF / AIA Materials Pledge (groupId = 1)
                const cmfDocTypes = allDocumentTypes.filter(doc => doc.groupId === 1);
                const allCmfOptions = cmfDocTypes.map(doc => ({
                    id: doc.documentTypeId.toString(),
                    name: doc.name
                }));

                // Define the names of top items to show first
                const topItemNames = [
                    'Environmental Product Declaration (EPD)',
                    'Material ingredient disclosure, other',
                    'Low-emitting certification, other'
                ];

                // Separate top items and remaining items
                const topItems: SelectData[] = [];
                const remainingItems: SelectData[] = [];

                allCmfOptions.forEach(option => {
                    if (topItemNames.includes(option.name)) {
                        topItems.push(option);
                    } else {
                        remainingItems.push(option);
                    }
                });

                // Sort top items in the specified order
                const sortedTopItems = topItemNames
                    .map(name => topItems.find(item => item.name === name))
                    .filter(item => item !== undefined) as SelectData[];

                // Sort remaining items alphabetically
                remainingItems.sort((a, b) => a.name.localeCompare(b.name));

                // Combine: top items + divider + remaining items
                options = [
                    ...sortedTopItems,
                    {
                        id: 'divider-cmf',
                        name: '',
                        disabled: true,
                        className: DOCUMENT_TYPE_DIVIDER,
                    },
                    ...remainingItems
                ];
                break;
            }
            case '2': { // LEED v5 (groupId = 3)
                const leedDocTypes = allDocumentTypes.filter(doc => doc.groupId === 3);

                // Separate LEED types by IsInTC status (from leedDocumentTypeData)
                const notInTC: SelectData[] = [];
                const inTC: SelectData[] = [];

                leedDocTypes.forEach(leedDoc => {
                    const option = {
                        id: leedDoc.documentTypeId.toString(),
                        name: leedDoc.name
                    };

                    if (leedDoc.leedDocumentTypeData?.isInTC) {
                        inTC.push(option);
                    } else {
                        notInTC.push(option);
                    }
                });

                // Sort each group alphabetically
                notInTC.sort((a, b) => a.name.localeCompare(b.name));
                inTC.sort((a, b) => a.name.localeCompare(b.name));

                // Combine: not in TC first + divider + then in TC
                options = [
                    ...notInTC,
                ];

                // Only add divider if both groups have items
                if (notInTC.length > 0 && inTC.length > 0) {
                    options.push({
                        id: 'divider-leed',
                        name: '',
                        disabled: true,
                        className: DOCUMENT_TYPE_DIVIDER,
                    });
                }

                options.push(...inTC);
                break;
            }
            case '3': { // Other (groupId = 2)
                const otherDocTypes = allDocumentTypes.filter(doc => doc.groupId === 2);

                // Map GetDocumentTypesResponse to SelectData and sort alphabetically
                options = otherDocTypes
                    .map(doc => ({
                        id: doc.documentTypeId.toString(),
                        name: doc.name
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                break;
            }
            case '4': {
                const leedLemDocTypes = allDocumentTypes.filter(doc => doc.groupId === 4);

                // Convert all LEED LEM types to options and sort alphabetically
                options = leedLemDocTypes
                    .map(leedDoc => ({
                        id: leedDoc.documentTypeId.toString(),
                        name: leedDoc.name
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                break;
            }
            default:
                options = [];
        }

        return options;
    };

    return (
        <Form onFinish={onSubmit} form={form} onFinishFailed={onFailed}>
            <BoldText weight={400} size={13} height={16} color={blueDark01}>
                Upload {uploadType === 'document' ? uploadType : 'images'}
            </BoldText>
            {uploadType === 'document' && (
                <UploadFormSubText weight={400} size={12} height={14} color={blueDark01}>
                    Upload {uploadType}s to your project
                </UploadFormSubText>
            )}
            <UploadFileContainer>
                <CustomInputUploadFileForm label="" name="isConfidential" Component={budgetFile} />
            </UploadFileContainer>
            <DocumentNameInput
                label={uploadType === 'document' ? `Document name ` : `Image name`}
                htmlFor="document_name"
                name="documentName"
                onChange={changeDocumentName}
            />
            {uploadType === 'picture' && (
                <AlternativeTextInput
                    label="Alternative text"
                    suffix="(optional)"
                    name="alternativeText"
                    placeholder="Alternative text"
                />
            )}
            {uploadType === UploadType.DOCUMENT && (
                <UploadDocumentTypeSelectContainer>
                    <CustomSelectWithSearch
                        label="Document type"
                        id="type_of_document_types"
                        name="typeOfDocumentTypeId"
                        options={DefinedDocumentTypes}
                        placeholder="Select one"
                        rules={[{ required: true, message: '' }]}
                        block
                        required
                        props={{
                            onChange: handleCategoryChange
                        }}
                    />
                    {showSecondDropdown && (
                        <div className="no-margin-wrapper">
                            <CustomSelectWithSearch
                                id="document_type"
                                name="documentTypeId"
                                options={getDocumentTypeOptions().map(opt =>
                                    opt.disabled && opt.className === DOCUMENT_TYPE_DIVIDER
                                        ? { ...opt, name: <Divider style={{ margin: '8px 0', borderColor: '#D9D9D9' }} /> as any }
                                        : opt
                                )}
                                placeholder="Select one"
                                rules={[
                                    { required: true, message: '' },
                                    {
                                        validator: (_: any, value: any) => {
                                            if (value === 'divider-cmf' || value === 'divider-leed') {
                                                return Promise.reject(new Error('Invalid selection'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                block
                                required
                            />
                        </div>
                    )}
                </UploadDocumentTypeSelectContainer>
            )}
            {uploadType === 'document' && (
                <DatePickerContainer>
                    <CustomInputUploadFileForm
                        Component={DatePicker}
                        label="Expiration date"
                        suffix="(optional)"
                        name="expirationOn"
                        props={{
                            placeholder: 'MM/DD/YYYY',
                            format: 'MM/DD/YYYY',
                            disabledDate: (current: any) => {
                                return current && current.format('YYYY') < '2015';
                            },
                            suffixIcon: (
                                <Image
                                    src="src/assets/images/calendar-day.svg"
                                    preview={false}
                                    alt="calendar"
                                />
                            ),
                        }}
                        block
                    />
                </DatePickerContainer>
            )}
            <DescriptionInput
                name="description"
                label="Description"
                suffix="(optional)"
                placeholder="Add description"
            />
            <SaveUploadsButton
                className={uploadType === UploadType.PICTURE ? 'pictureSaveBtn' : ''}
                htmlType="submit"
            >
                Save
            </SaveUploadsButton>
        </Form>
    );
};

export default UploadFileForm;
